import { getCurrentMonth, getCurrentYear } from "../utils/render"
import moment from "moment"

/**
 * @description Hello **world!**
 *
 * @typedef {{
 *  date: string
 *  image: string
 *  key: string
 *  month: string
 *  pinned: boolean
 *  platform: string
 *  reflection: string
 *  review: string
 *  tags: string[]
 *  text: string
 *  thumbnailImage: string
 *  type: string
 *  year: string|number
 *  __typename: string
 * }}
 * UserEntry
 *
 * @typedef {{
 *  entryKey: string
 *  question?: string
 *  questionResponse?: string
 *  note?: string
 *  id?: string
 *  updatedAt?: number
 *  __typename?: string
 * }}
 * ReflectionObject
 *
 * @typedef {{
 *  question: string
 *  subtext: string
 *  response: string
 *  type: string
 *  fieldOnly?: boolean
 *  hideElements?: string[]
 *  __typename: string
 * }}
 * ReviewQuestion
 *
 * @typedef {{
 *  note: string
 *  rank: number
 *  __typename: string
 * }}
 * ReviewRanking
 *
 * @typedef {{
 *  id: string
 *  date: string
 *  version: number
 *  draft: boolean
 *  year: string|number
 *  saved: string,
 *  lowlightsNotes: ReflectionObject[]
 *  categoryQuestions: {
 *    body: ReviewQuestion[]
 *    love: ReviewQuestion[]
 *    soul: ReviewQuestion[]
 *    mind: ReviewQuestion[]
 *    play: ReviewQuestion[]
 *    work: ReviewQuestion[]
 *    __typename: string
 *  }
 *  highlightsQuestions: ReviewQuestion[]
 *  favorites: ReviewQuestion[]
 *  lowlightsQuestions: ReviewQuestion[]
 *  revisionQuestions: ReviewQuestion[]
 *  summaryQuestions: ReviewQuestion[]
 *  rankings: {
 *    body: ReviewRanking
 *    love: ReviewRanking
 *    soul: ReviewRanking
 *    mind: ReviewRanking
 *    play: ReviewRanking
 *    work: ReviewRanking
 *    __typename: string
 *  }
 *  __typename: string
 * }}
 * YearlyReviewObject
 *
 */

export const TEST_VALUE_DATA = {
  a: "Test Value  A",
  b: "Test Value B",
  __typename: "TestValue",
}

export const ERROR_DATA = {
  title: "Error",
  code: "",
  message: "",
  internal: "",
  field: "",
  ui: "",
  __typename: "ApplicationError",
}

/**
 * An object representing the form of a standard user entry.
 *
 * @type {UserEntry}
 */
export const CURRENT_ENTRY_DATA = {
  key: "CREATE$", //$ arbitrary special char selected
  date: moment().toISOString(true),
  tags: [],
  type: "draft",
  text: "",
  image: "",
  pinned: false,
  platform: "web",
  month: getCurrentMonth(),
  year: getCurrentYear(),
  review: "",
  reflection: "",
  thumbnailImage: "",
  __typename: "CurrentEntry",
}

/**
 * An object representing the structure of a reflection in a review's data
 *
 * @type {ReflectionObject}
 */
export const REFLECTION_OBJECT = {
  entryKey: "",
  question: "",
  questionResponse: "",
  note: "",
  updatedAt: 0,
  __typename: "",
}

/**
 * @type {typeof REFLECTION_OBJECT}
 */
export const LOWLIGHT_REFLECTION_OBJECT = REFLECTION_OBJECT

export const MONTHLY_REVIEW_DATA = {
  id: null,
  draft: true,
  date: null,
  month: null,
  year: null,
  index: null,
  version: 2.0,
  highlightsNote: "",
  lowlightsNotes: [], //question, questionResponse, note, entryKey, updatedAt
  rankings: {
    mind: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    body: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    soul: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    work: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    play: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    love: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    __typename: "ReviewRankings",
  },
  summarize: "",
  forward: "",
  saved: "",
  entries: [],
  __typename: "MonthlyReview",
}

/**
 * An object representing the structure of a yearly review
 *
 * @type {YearlyReviewObject}
 */
export const YEARLY_REVIEW_DATA = {
  id: null,
  draft: true,
  date: null,
  year: null,
  version: 2.0,
  lowlightsNotes: [],
  saved: "",
  favorites: [
    {
      question: "Favorite Song:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
    {
      question: "Favorite Book:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
    {
      question: "Favorite Movie or Show:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
    {
      question: "Favorite Meal:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
    {
      question: "Favorite Place Visited:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
    {
      question: "Favorite Object:",
      subtext: "",
      response: "",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
  ],
  rankings: {
    mind: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    body: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    soul: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    work: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    play: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    love: {
      rank: 5,
      note: "",
      __typename: "ReviewRankingType",
    },
    __typename: "ReviewRankings",
  },
  highlightsQuestions: [
    {
      question: "Are there any patterns in your Highlights?",
      subtext:
        "For example, were there similarities around what you were doing, where you were or who you were with?",
      response: "",
      type: "multiline",
      __typename: "HighlightsQuestion",
    },
  ],
  lowlightsQuestions: [
    {
      question: "What is something you regret from the past year?",
      subtext: "",
      response: "",
      type: "multiline",
      __typename: "LowlightsQuestion",
    },
    {
      question: "",
      subtext:
        "Now write yourself a short note in which you forgive yourself. Allow yourself to let go of this regret and move forward.",
      response: "",
      type: "multiline",
      __typename: "LowlightsQuestion",
    },
  ],
  categoryQuestions: {
    mind: [
      {
        question: "What inspired you most this year?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    body: [
      {
        question: "What healthy habits did you start this year?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
      {
        question: "What are some unhealthy habits you picked up?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    soul: [
      {
        question: "What gave you purpose this year?",
        subtext: "",
        response: "",
        type: "singleline",
        __typename: "CategoryRankingQuestion",
      },
      {
        question: "Because...",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    work: [
      {
        question: "What new skills did you learn this year?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
      {
        question: "This year my biggest accomplishment was:",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    play: [
      {
        question: "What activities sparked joy for you this year?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    love: [
      {
        question: "Who are you most grateful for this year?",
        subtext:
          "Tip: Take a moment and let them know! It can be as simple as a text message, a phone call, or an invite to coffee so you can express your gratitude in person.",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
      {
        question:
          "Which relationships could have used more time, love, or attention this past year?",
        subtext: "",
        response: "",
        type: "multiline",
        __typename: "CategoryRankingQuestion",
      },
    ],
    __typename: "CategoryRankingQuestions",
  },
  revisionQuestions: [
    {
      question: "Looking ahead.",
      subtext: "What are you most looking forward to in the year ahead?",
      response: "",
      type: "multiline",
      fieldOnly: true,
      hideElements: ["question", "subtext"],
      __typename: "RevisionQuestions",
    },
  ],
  summaryQuestions: [
    {
      question: "This past year in one or two words:",
      subtext: "",
      response: "Draft",
      type: "singleline",
      __typename: "SummaryQuestion",
    },
  ],
  __typename: "YearlyReview",
}

export const INIT_USER_DATA = {
  premiumPlan: "plan-free",
  premiumStatus: false,
  premiumSubscriptionId: "",
  premiumPurchaseSource: "",
  __typename: "UserData",
}

export const fixLowlightTypename = reviewData => {
  const hasLowlights =
    reviewData.lowlightsNotes && reviewData.lowlightsNotes.length > 0
  if (hasLowlights) {
    reviewData.lowlightsNotes.forEach((data, index) => {
      reviewData.lowlightsNotes[index] = {
        ...data,
        id: index,
        entryKey: !!data.entryKey ? data.entryKey : "",
        updatedAt: data.updatedAt ? data.updatedAt : 0,
        __typename: "LowlightNote",
      }
    })
  }
  // debugger
  return reviewData
}
